import { useDispatch, useSelector } from 'react-redux';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import MenuOutlined from '@mui/icons-material/MenuOutlined';
import { HEADER_HEIGHT, SHELL_ICON_SIZE } from '@/theme';
import type { RootState } from '@/store';
import CommandK from '../../components/CommandK/CommandK/CommandK';
import HealthIndicator from '../../components/HealthIndicator/HealthIndicator';
import AccountMenu from './AccountMenu';
import AppBreadcrumb from './AppBreadcrumb';
import ColorModeToggle from './ColorModeToggle';
import ThemePresetMenu from './ThemePresetMenu';
import FontSizeMenu from './FontSizeMenu';

interface AppHeaderProps {
  onLogout?: () => void;
}

export default function AppHeader({ onLogout }: AppHeaderProps) {
  const dispatch = useDispatch();
  const sidebarShow = useSelector((state: RootState) => Boolean(state.sidebarShow));
  const asideShow = useSelector((state: RootState) => Boolean(state.asideShow));
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 0 });

  return (
    <AppBar
      position='sticky'
      color='inherit'
      elevation={scrolled ? 2 : 0}
      sx={{
        // Shell surface, not paper: the chrome frames the page instead of blending into its cards.
        bgcolor: 'zuul.shell',
        borderBottom: 1,
        borderColor: 'divider',
        zIndex: (theme) => theme.zIndex.appBar,
      }}
    >
      <Toolbar
        variant='dense'
        disableGutters
        sx={{ minHeight: HEADER_HEIGHT, px: 2, gap: 1, '& .MuiSvgIcon-root': { fontSize: SHELL_ICON_SIZE } }}
      >
        <Tooltip title={sidebarShow ? 'Hide navigation' : 'Show navigation'}>
          <IconButton
            edge='start'
            aria-label={sidebarShow ? 'Hide navigation' : 'Show navigation'}
            onClick={() => dispatch({ type: 'set', sidebarShow: !sidebarShow })}
          >
            <MenuOutlined />
          </IconButton>
        </Tooltip>
        <Box sx={{ display: { xs: 'none', md: 'block' }, ml: 1 }}>
          <AppBreadcrumb />
        </Box>
        <Box sx={{ flex: 1 }} />
        <HealthIndicator />
        <CommandK />
        <Divider orientation='vertical' flexItem sx={{ mx: 0.5, my: 1.5 }} />
        <ThemePresetMenu />
        <FontSizeMenu />
        <ColorModeToggle />
        <AccountMenu onLogout={onLogout} />
        <Divider orientation='vertical' flexItem sx={{ mx: 0.5, my: 1.5 }} />
        <Tooltip title={asideShow ? 'Hide side panel' : 'Show side panel'}>
          <IconButton
            edge='end'
            aria-label={asideShow ? 'Hide side panel' : 'Show side panel'}
            onClick={() => dispatch({ type: 'set', asideShow: !asideShow })}
          >
            {asideShow ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </Tooltip>
      </Toolbar>
    </AppBar>
  );
}
